import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CCard, CCardGroup } from "@coreui/react";
import { toast } from "react-toastify";
import { useTranslationContext } from "@/context/TranslationContext";
import DummyUserForm from "../../dummyUserForm";
import { dummyUser } from "@/constants/translation/dummyUser.translation";
import { breadcrumb_key, messages } from "@/constants/translation/common.translation";
import DummyUserValidationClass from "@/helpers/validations/dummyUser";
import { dummyUser as dummyUserRoutes } from "@/libs/routes";
import { useGetDummyUser, useUpdateDummyUser } from "@/hooks/useDummyUser";

/**
 * Component for editing an existing Dummy User.
 * @returns {JSX.Element} The rendered EditDummyUser component.
 */
const EditDummyUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const t = useTranslationContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: response, isLoading: isDataLoading } = useGetDummyUser(id, undefined, () => {
    toast.error(t(messages.toast.dummyUser.dataFetchFailed));
  });

  const { mutate: doUpdateDummyUser } = useUpdateDummyUser(
    (response) => {
      toast.success(response?.message || t(messages.toast.dummyUser.updated));
      navigate(dummyUserRoutes.list);
    },
    (error) => {
      toast.error(error?.response?.data?.message || t(messages.toast.dummyUser.updateFailed));
      setIsSubmitting(false);
    }
  );

  const handleUpdateDummyUser = (values) => {
    setIsSubmitting(true);
    doUpdateDummyUser({
      uuid: id,
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.email,
      house_number: values.house_number,
      street: values.street,
      city: values.city,
      country: values.country,
      zip_code: values.zip_code,
      latitude: values.latitude,
      longitude: values.longitude,
    });
  };

  const dummyUserData = response?.data;

  const initialValues = useMemo(() => {
    const [firstName = "", ...rest] = (dummyUserData?.full_name || "").split(" ");
    return {
      first_name: firstName,
      last_name: rest.join(" "),
      email: dummyUserData?.email || "",
      house_number: dummyUserData?.house_number || "",
      street: dummyUserData?.street || "",
      city: dummyUserData?.city || "",
      country: dummyUserData?.country || "",
      zip_code: dummyUserData?.zip_code || "",
      latitude: dummyUserData?.latitude ?? "",
      longitude: dummyUserData?.longitude ?? "",
    };
  }, [dummyUserData]);

  const validationSchema = useMemo(() => DummyUserValidationClass.dummyUserValidation(t), [t]);

  if (isDataLoading) {
    return (
      <CCardGroup>
        <CCard className="p-4">
          <div className="text-center">
            <p>{t(dummyUser.edit.main_heading)}...</p>
          </div>
        </CCard>
      </CCardGroup>
    );
  }

  return (
    <CCardGroup>
      <CCard className="p-4">
        <h1 className="text-center">{t(dummyUser.edit.main_heading)}</h1>
        <DummyUserForm
          initialValues={initialValues}
          onSubmit={handleUpdateDummyUser}
          isSubmitting={isSubmitting}
          validationSchema={validationSchema}
          isEdit
        />
      </CCard>
    </CCardGroup>
  );
};

EditDummyUser.breadCrumbArray = [
  {
    name: breadcrumb_key.dummyUser?.list,
    pathname: dummyUserRoutes.list,
    active: false,
  },
  {
    name: breadcrumb_key.dummyUser?.edit,
    pathname: "",
    active: true,
  },
];

export default EditDummyUser;
